import { Radio } from '../Radio';
import { FixedSizeList as List } from 'react-window';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Flex,
  Text,
  Checkbox,
  Button,
  ScrollArea,
  LoadingOverlay,
  ActionIcon,
  Alert,
  Box
} from '@mantine/core';
import { useViewportSize } from '@mantine/hooks';
import {
  useCreateCustomField,
  useDeleteCustomField,
  useGetCustomFields
} from '../../../../../services/funds.service';
import toast from 'react-hot-toast';
import { extractApiError } from '../../../../../utils';
import { Fields } from '../Fields';
import { cloneDeep } from 'lodash';
import { SheetIcon, CaretDownIcon } from './icons';
import {
  TableStyle,
  FundChildrenContainer,
  FundContainer,
  TableName,
  Fund,
  CaretStyled,
  SheetStyled,
  FundName,
  Body
} from './style';
import {
  TbInfoCircle,
  TbLayoutSidebarLeftExpand,
  TbLayoutSidebarRightExpand,
  TbPlus
} from 'react-icons/tb';
import RenderColumns from './RenderColumns';
import RenderColumnLabels from './RenderColumnLabels';
import extractionStore from '../../../store';
import { REPORT_NAMINGS, REPORT_TYPES } from '../../../../../consts/reportTypes';
import { PiCursor } from 'react-icons/pi';
import {
  useCreateGlobalCustomField,
  useDeleteGlobalCustomField
} from '../../../../../services/workflow.services';
import { NewDeleteConfirmModal } from '../../../../../components/NewDeleteConfirmModal';

// Detects vertical key-value PDF layout: each column's values occupy distinct rowIds
// (no two columns share a rowId). When true, all values belong to one logical record.
const isVerticalKeyValueLayout = columns => {
  if (!columns?.length) return false;

  const seenRowIds = new Set();
  const rowCountsPerColumn = [];

  for (const col of columns) {
    const uniqueRowIds = new Set();

    for (const val of col.values || []) {
      if (val.rowId && val.columnId) {
        if (seenRowIds.has(val.rowId)) return false;
        seenRowIds.add(val.rowId);
        uniqueRowIds.add(val.rowId);
      }
    }

    if (uniqueRowIds.size > 0) {
      rowCountsPerColumn.push(uniqueRowIds.size);
    }
  }

  if (!rowCountsPerColumn.length) return false;

  // Treat as vertical key/value only when mappings are compact and mostly single-valued.
  // This avoids collapsing real multi-row tables into one row.
  const mappedColumnCount = rowCountsPerColumn.length;
  const totalMappedRows = rowCountsPerColumn.reduce((sum, count) => sum + count, 0);
  const mostlySingleValuedColumns =
    rowCountsPerColumn.filter(count => count === 1).length / mappedColumnCount >= 0.8;
  const maxRowsInAnyColumn = Math.max(...rowCountsPerColumn);
  const compactPattern = seenRowIds.size <= mappedColumnCount + 2;

  return (
    mostlySingleValuedColumns && maxRowsInAnyColumn <= 2 && compactPattern && totalMappedRows > 1
  );
};

const hasTextContent = text => text != null && String(text).trim() !== '';

const getNonEmptyRowIds = ({ columns }) => {
  const rowIdsWithContent = new Set();

  columns?.forEach(column => {
    column.values?.forEach(value => {
      // `columnId` can be null for cells the API returned without a grid
      // position (e.g. "Fund name" with x:null/y:null) — they still carry
      // text we need to show in the sidebar, so don't require columnId here.
      if (value?.rowId != null && hasTextContent(value.text)) {
        rowIdsWithContent.add(value.rowId);
      }
    });
  });

  return [...rowIdsWithContent].sort((a, b) => a - b);
};

const renderInfo = (action, isStructured) => {
  if (action === 'SemiAutomatic') return null;

  let message = null;
  if (action === 'Automatic') {
    message =
      'Select the required fields and use prompt messages when needed for better extraction.';
  } else if (isStructured) {
    message = 'Select a required field and match it to a column from the table on the right.';
  } else {
    message =
      'Select a column from the AIF’s table and highlight cells using your mouse or keyboard.';
  }

  return (
    <Alert
      w="fit-content"
      variant="outline"
      color="blue"
      p="12px"
      mb="16px"
      icon={<TbInfoCircle />}
    >
      {message}
    </Alert>
  );
};

export const Sidebar = ({
  selectedLookupTable,
  onSelectTable,
  isLoadingAutoExtract,
  sheetId,
  action,
  onChangeAction,
  onSelectFund,
  funds,
  onPickFund,
  refetchCategories,
  pickedChildFund,
  onPickChildFund,
  onEnableValue,
  activeFundChildValue,
  onDeleteSelectedCell,
  selectedFund,
  pickedFund,
  categories,
  category,
  isLoaging,
  error,
  isStructured,
  onSelectColumn,
  selectedColumn,
  isLookup,
  onClearValuesColumn,
  onClearFundValues,
  onFocusLocation,
  onSearch,
  onDeleteRow,
  setSelectedFund,
  setPickedFund,
  hasChanges,
  openPopup,
  isIndividualFund,
  onFullSelectionStructuredCells,
  openUpdateCustomTableDrawer,
  openCustomTableDrawer,
  onSelectCustomTable,
  isLockedPeriod,
  extractionType
}) => {
  const {
    isSidebarExpanded,
    toggleExpandSidebar,
    selectedExtractItems: selectedExtract,
    setSelectedExtract,
    reportType
  } = extractionStore;

  const inputRef = useRef();
  const _category = useMemo(() => {
    const foundCategory = cloneDeep(categories?.find(ctg => ctg?.name == category?.name));

    if (!funds || !foundCategory) return null;

    const _funds = cloneDeep(funds);
    const fieldIsEmptyValues = {};

    if (isLookup)
      for (let j = 0; j < _funds?.length; j++) {
        if (foundCategory?.name === 'Lookup') {
          for (let c = 0; c < _funds[j].columns?.length; c++) {
            const column = cloneDeep(_funds[j].columns[c]);
            const key = `${_funds[j]?.name}-${column.name}`;
            const isEmpty = !column.values.some(value => hasTextContent(value.text));

            fieldIsEmptyValues[key] = [...(fieldIsEmptyValues[key] || []), isEmpty];
          }
        }
      }
    else
      for (let i = 0; i < _funds?.length; i++) {
        for (let j = 0; j < _funds[i].tables?.length; j++) {
          if (_funds[i].tables[j]?.name == foundCategory?.name) {
            for (let c = 0; c < _funds[i].tables[j].columns?.length; c++) {
              const column = cloneDeep(_funds[i].tables[j].columns[c]);
              const isEmpty = !column.values.some(value => hasTextContent(value.text));

              fieldIsEmptyValues[column.name] = [
                ...(fieldIsEmptyValues[column.name] || []),
                isEmpty
              ];
            }
          }
        }
      }

    for (let k = 0; k < foundCategory?.tables?.length; k++) {
      for (let l = 0; l < foundCategory.tables[k].columns.length; l++) {
        const column = foundCategory.tables[k].columns[l];
        const key = isLookup ? `${foundCategory?.tables[k]?.name}-${column?.name}` : column.name;
        column.isEmpty = fieldIsEmptyValues[key]?.every(boolean => boolean);

        if (isLookup) column.table = foundCategory?.tables[k]?.name;
      }
    }

    return foundCategory;
  }, [funds, category, categories, isLookup]);

  const [selectedColumnId, setSelectedColumnId] = useState(null);
  const [selectedFieldId, setSelectedFieldId] = useState(null);
  const [openCustomFieldTypeList, setOpenCustomFieldTypeList] = useState(false);
  const [customFieldType, setCustomFieldType] = useState(null);
  const [newField, setNewField] = useState();
  const [selectedTableName, setSelectedTableName] = useState();

  // Clamp react-window's hardcoded List height (500 / 1000 / 144) so its
  // internal horizontal scrollbar stays inside the ScrollArea's visible
  // viewport. GridTable wrapper = vh - 280, sidebar headers above the
  // List = ~140; offset 420 keeps the List bottom at/above the fold.
  const { height: viewportHeight } = useViewportSize();
  const maxListHeight = Math.max(140, viewportHeight - 480);

  const { data: customFields, refetch } = useGetCustomFields({
    params: {
      category: category?.name,
      sheet_id: sheetId
    },
    queryParams: {
      enabled: !!sheetId && !!category?.name
    }
  });

  const createCustomField = useCreateCustomField();
  const createGlobalCustomField = useCreateGlobalCustomField();
  const deleteCustomField = useDeleteCustomField();
  const deleteGlobalCustomField = useDeleteGlobalCustomField();

  const isCustomReportType = reportType === REPORT_TYPES.CUSTOM;

  const isAllFundsExpanded = useMemo(() => {
    return funds?.length === pickedFund?.length;
  }, [funds, pickedFund]);

  const isAllFundsSelected = useMemo(() => {
    return funds?.length === selectedFund?.length;
  }, [funds, selectedFund]);

  const onPostRequest = () => {
    refetch();
    refetchCategories();
  };

  const handleHighlightAllFunds = e => {
    e?.preventDefault();
    e?.stopPropagation();

    if (action === 'Automatic') return;

    if (isAllFundsSelected) {
      setSelectedFund([]);
    } else {
      if (funds?.length) setSelectedFund([...funds.map(fund => fund.id)]);
    }
  };

  const handleExpandAllFunds = e => {
    e?.preventDefault();
    e?.stopPropagation();

    if (isAllFundsExpanded) {
      setPickedFund([]);
      onPickChildFund([]);
    } else {
      if (funds?.length) setPickedFund([...funds.map(fund => fund.id)]);
      if (!isLookup)
        onPickChildFund(
          funds
            .map(item => item.tables)
            .flat()
            .map(item => item.id)
        );
    }
  };

  const handleChange = e => {
    setNewField(e.target.value);
  };

  const handleDeleteCustomField = useCallback(
    async (id, callback) => {
      if (hasChanges) {
        const userResponse = await openPopup();

        if (!userResponse) return;
      }

      if (!selectedFieldId) {
        setSelectedFieldId(id);
        return;
      }

      if (isCustomReportType)
        deleteGlobalCustomField.mutate(selectedFieldId, {
          onSuccess: () => {
            onPostRequest();
            setSelectedExtract(selectedExtract.filter(item => item.id !== selectedFieldId));
            setSelectedFieldId(null);
            callback?.();
          },
          onError: err => {
            toast.error(extractApiError(err, 'Unable to delete this custom report field.'));
          }
        });
      else
        deleteCustomField.mutate(selectedFieldId, {
          onSuccess: () => {
            onPostRequest();
            if (isLookup) {
              setSelectedExtract(selectedExtract.filter(item => item.uuid !== selectedFieldId));
            } else {
              setSelectedExtract(selectedExtract.filter(item => item.id !== selectedFieldId));
            }
            setSelectedFieldId(null);
          },
          onError: err => {
            toast.error(extractApiError(err, 'Unable to delete this custom field.'));
          }
        });
    },
    [selectedExtract, hasChanges, openPopup, isCustomReportType, selectedFieldId]
  );

  const handleCreateCustomField = useCallback(
    async (e, tableId) => {
      e?.preventDefault();
      if (!newField) return;

      if (hasChanges) {
        const userResponse = await openPopup();

        if (!userResponse) return;
      }

      if (isCustomReportType)
        createGlobalCustomField.mutate(
          {
            tableId,
            data: [{ name: newField, type: customFieldType }]
          },
          {
            onSuccess: () => {
              setSelectedTableName(null);
              setCustomFieldType(null);
              setNewField();
              onPostRequest();
            },
            onError: err => {
              toast.error(extractApiError(err, 'Unable to add this custom field.'));
            }
          }
        );
      else
        createCustomField.mutate(
          {
            name: newField,
            category: category?.name,
            table: isLookup || isCustomReportType ? selectedTableName : category?.tables?.[0]?.name,
            sheet_id: sheetId,
            type: customFieldType
          },
          {
            onSuccess: () => {
              setSelectedTableName(null);
              setCustomFieldType(null);
              setNewField();
              onPostRequest();
            },
            onError: err => {
              toast.error(extractApiError(err, 'Unable to add this custom field.'));
            }
          }
        );
    },
    [newField, category?.name, sheetId, customFieldType, isCustomReportType]
  );

  const showDefaultAddCustomTableButton =
    isLookup && category && action && !isLockedPeriod && !isCustomReportType;

  const renderSidebarActions = () => {
    return (
      <Flex
        align="center"
        key={'sidebar-actions' + isSidebarExpanded ? 'opened' : 'closed'}
        gap="8px"
      >
        <ActionIcon
          id="grid-table-sidebar-toggle"
          variant="transparent"
          c="#ADB5BD"
          onClick={() => toggleExpandSidebar(!isSidebarExpanded)}
        >
          {!isSidebarExpanded ? (
            <TbLayoutSidebarLeftExpand size={20} />
          ) : (
            <TbLayoutSidebarRightExpand size={20} />
          )}
        </ActionIcon>
      </Flex>
    );
  };

  const renderEmptyState = () => {
    return (
      <Flex justify="center" align="center" h={400} direction="column">
        <Box
          display="flex"
          w={40}
          h={40}
          bd="1px solid #CED4DA"
          mb={12}
          sx={{ borderRadius: '20px', justifyContent: 'center', alignItems: 'center' }}
        >
          <PiCursor size={18} color="#CED4DA" />
        </Box>
        <Text c="gray.5" fz="14px">
          Please select Data Category first
        </Text>
      </Flex>
    );
  };

  useEffect(() => {
    if (customFieldType) inputRef.current?.focus();
  }, [inputRef, customFieldType]);

  // auto-apply Highlight/Expand when user switches to "Manual" extraction or changes the "Data Category"
  useEffect(() => {
    if (action === 'Manual' && !isAllFundsSelected && !isAllFundsExpanded) {
      // when DataExtraction component is unmounted it resets the selectedFund and overrides
      // highlight and expand logic, so we put a small delay here using setTimeout
      setTimeout(() => {
        handleHighlightAllFunds();
        handleExpandAllFunds();
      });
    }
  }, [action, category?.name, funds]);

  return (
    <>
      <ScrollArea
        id="grid-table-sidebar-scroll-area"
        scrollbarSize={14}
        scrollHideDelay={500}
        p="16px"
        type="auto"
        sx={() => ({
          overflow: 'auto'
        })}
        bg="white"
        w="100%"
        h="100%"
        mah="100%"
        pos="relative"
      >
        <Flex align="center" mb="16px" justify="space-between">
          <Text id="grid-table-sidebar-title" c="dark.7" fz="16px">
            Extracted Data
          </Text>
          {renderSidebarActions()}
        </Flex>
        {!category && renderEmptyState()}
        {extractionType?.id && renderInfo(extractionType?.id, isStructured)}
        {funds?.length > 0 && (
          <Body>
            {category && action && (
              <>
                <Fields
                  key={action + _category?.name}
                  activeFundChildValue={activeFundChildValue}
                  onEnableValue={onEnableValue}
                  selectedLookupTable={selectedLookupTable}
                  onSelectTable={onSelectTable}
                  isLoadingAutoExtract={isLoadingAutoExtract}
                  data={customFields}
                  category={_category}
                  onSelectFund={onSelectFund}
                  error={error}
                  onChangeAction={onChangeAction}
                  isStructured={isStructured}
                  isLookup={isLookup}
                  isManual={action === 'Manual'}
                  handleDeleteCustomField={handleDeleteCustomField}
                  selectedColumn={selectedColumn}
                  setSelectedColumnId={setSelectedColumnId}
                  onClearFundValues={onClearFundValues}
                  onSelectColumn={onSelectColumn}
                  selectedColumnId={selectedColumnId}
                  customFieldType={customFieldType}
                  openCustomFieldTypeList={openCustomFieldTypeList}
                  handleCreateCustomField={handleCreateCustomField}
                  setCustomFieldType={setCustomFieldType}
                  setOpenCustomFieldTypeList={setOpenCustomFieldTypeList}
                  inputRef={inputRef}
                  handleChange={handleChange}
                  newField={newField}
                  selectedTableName={selectedTableName}
                  setSelectedTableName={setSelectedTableName}
                  deleteCustomField={
                    isCustomReportType ? deleteGlobalCustomField : deleteCustomField
                  }
                  onFullSelectionStructuredCells={onFullSelectionStructuredCells}
                  sheetId={sheetId}
                  categoryName={category?.name}
                  tableName={category?.tables?.[0]?.name}
                  isExtrationOptionShown={action === 'Automatic'}
                  openCustomTableDrawer={openCustomTableDrawer}
                  openUpdateCustomTableDrawer={openUpdateCustomTableDrawer}
                  onSelectCustomTable={onSelectCustomTable}
                  isLockedPeriod={isLockedPeriod}
                  isCustomReportType={isCustomReportType}
                  isLoadingCreateCustomField={
                    createCustomField.isLoading || createGlobalCustomField.isLoading
                  }
                />

                {isCustomReportType && (
                  <Button
                    id="grid-table-sidebar-add-new-table"
                    variant="outline"
                    leftSection={<TbPlus />}
                    size="xs"
                    h="27px"
                    onClick={openCustomTableDrawer}
                  >
                    Add New Table
                  </Button>
                )}
              </>
            )}

            <Flex align="center" justify="space-between" p="4px 0 12px">
              <Text fw="600" fz="14px">
                {reportType === REPORT_TYPES.CUSTOM
                  ? 'Tables'
                  : `All ${REPORT_NAMINGS.FUND[reportType]}s`}
              </Text>

              <Flex align="center" gap="10px">
                {showDefaultAddCustomTableButton && (
                  <Button
                    id="grid-table-sidebar-add-custom-table"
                    variant="subtle"
                    leftSection={<TbPlus />}
                    size="xs"
                    h="27px"
                    onClick={openCustomTableDrawer}
                  >
                    Add Custom Table
                  </Button>
                )}

                <Flex
                  id="grid-table-sidebar-highlight-all"
                  align="center"
                  onClick={handleHighlightAllFunds}
                  gap="8px"
                >
                  <Text fz="12px">Highlight All</Text>
                  <Checkbox
                    id="grid-table-sidebar-highlight-all-checkbox"
                    checked={isAllFundsSelected}
                    size="xs"
                  />
                </Flex>
                <Flex
                  id="grid-table-sidebar-expand-all"
                  align="center"
                  onClick={handleExpandAllFunds}
                  gap="8px"
                >
                  <Text fz="12px">Expand All</Text>
                  <Checkbox
                    id="grid-table-sidebar-expand-all-checkbox"
                    checked={isAllFundsExpanded}
                    size="xs"
                  />
                </Flex>
              </Flex>
            </Flex>

            <LoadingOverlay visible={isLoaging} zIndex={1000} overlayProps={{ blur: 2 }} />

            {!isLoaging &&
              funds?.map(item => {
                let lookupRows = [];

                if (pickedFund?.includes(item.id) && isLookup) {
                  lookupRows = getNonEmptyRowIds({ columns: item.columns });
                }

                return (
                  <FundContainer key={item.id}>
                    <Fund bgColor={item.backgroundColor} isActive={selectedFund?.includes(item.id)}>
                      <FundName onClick={() => onPickFund(item.id)}>
                        <CaretStyled
                          isRotate={pickedFund?.includes(item.id)}
                          isActive={selectedFund?.includes(item.id)}
                          color={item.color}
                        >
                          <CaretDownIcon />
                        </CaretStyled>
                        <SheetStyled color={item.color} isActive={selectedFund?.includes(item.id)}>
                          <SheetIcon />
                        </SheetStyled>
                        <p
                          id={`grid-table-sidebar-fund-name-${item.id}`}
                          title={isLookup ? `Total: ${lookupRows.length} rows` : ''}
                          onClick={() => {
                            if (!isStructured) {
                              onSearch(item.name);
                              setTimeout(() => {
                                onSearch('');
                              }, 2000);
                            }
                          }}
                        >
                          {item.name}
                        </p>
                      </FundName>
                      <Radio
                        id={`grid-table-sidebar-fund-radio-${item.id}`}
                        className="radio_btn"
                        isChecked={selectedFund?.includes(item.id)}
                        onClick={() => onSelectFund(item.id)}
                      />
                    </Fund>

                    {isLookup && pickedFund.includes(item.id) && (
                      <FundChildrenContainer
                        bgColor={item.backgroundColor}
                        isActive={selectedFund?.includes(item.id)}
                      >
                        <TableStyle>
                          {lookupRows.length <= 6 ? (
                            <>
                              <RenderColumnLabels
                                isStructured={isStructured}
                                error={error}
                                table={item}
                                onEnableValue={onEnableValue}
                                activeFundChildValue={activeFundChildValue}
                                isLookup={isLookup}
                                disabled={isLockedPeriod}
                                fundId={item.id}
                                onClearValuesColumn={onClearValuesColumn}
                                bgColor={
                                  selectedFund?.includes(item.id) ? item.backgroundColor : ''
                                }
                              />

                              {lookupRows.map((_, index) => (
                                <RenderColumns
                                  key={index}
                                  isIndividualFund={isIndividualFund}
                                  table={item}
                                  rows={lookupRows}
                                  index={index + 1}
                                  action={action}
                                  onDeleteSelectedCell={onDeleteSelectedCell}
                                  onFocusLocation={onFocusLocation}
                                  onDeleteRow={onDeleteRow}
                                />
                              ))}
                            </>
                          ) : (
                            <List
                              height={Math.min(funds.length === 1 ? 1000 : 144, maxListHeight)}
                              itemCount={lookupRows.length + 1}
                              itemSize={18}
                              width="100%"
                            >
                              {({ index, style }) =>
                                index === 0 ? (
                                  <RenderColumnLabels
                                    isStructured={isStructured && !isLookup}
                                    error={error}
                                    table={item}
                                    disabled={isLockedPeriod}
                                    onEnableValue={onEnableValue}
                                    activeFundChildValue={activeFundChildValue}
                                    fundId={item.id}
                                    onClearValuesColumn={onClearValuesColumn}
                                    bgColor={
                                      selectedFund?.includes(item.id) ? item.backgroundColor : ''
                                    }
                                    style={style}
                                  />
                                ) : (
                                  <RenderColumns
                                    table={item}
                                    isIndividualFund={isIndividualFund}
                                    rows={lookupRows}
                                    index={index}
                                    style={style}
                                    onDeleteSelectedCell={onDeleteSelectedCell}
                                    action={action}
                                    onFocusLocation={onFocusLocation}
                                    onDeleteRow={onDeleteRow}
                                  />
                                )
                              }
                            </List>
                          )}
                        </TableStyle>
                      </FundChildrenContainer>
                    )}

                    {!isLookup && pickedFund?.includes(item.id) && (
                      <FundChildrenContainer
                        bgColor={item.backgroundColor}
                        isActive={selectedFund?.includes(item.id)}
                      >
                        {item?.tables?.map(table => {
                          let rows = [];
                          if (pickedChildFund?.includes(table.id)) {
                            rows = getNonEmptyRowIds({ columns: table.columns });

                            if (rows.length > 1 && isVerticalKeyValueLayout(table.columns)) {
                              rows = ['__vertical__'];
                            }
                          }

                          return (
                            <TableStyle key={table.id}>
                              <TableName
                                id={`grid-table-sidebar-child-table-${table.id}`}
                                isPickedFound={pickedFund?.includes(item.id)}
                                isActive={selectedFund?.includes(item.id)}
                              >
                                <FundName onClick={() => onPickChildFund(table.id)}>
                                  <CaretStyled
                                    isPickedFound={selectedFund?.includes(item.id)}
                                    isRotate={pickedChildFund?.includes(table.id)}
                                    color={item.color}
                                  >
                                    <CaretDownIcon />
                                  </CaretStyled>
                                  <p
                                    id={`grid-table-sidebar-child-table-name-${table.id}`}
                                    title={`Total: ${rows.length} rows`}
                                  >
                                    {table.name}
                                  </p>
                                </FundName>
                              </TableName>
                              {pickedChildFund?.includes(table.id) && (
                                <>
                                  {rows.length <= 6 ? (
                                    <>
                                      <RenderColumnLabels
                                        isStructured={isStructured}
                                        error={error}
                                        table={table}
                                        disabled={isLockedPeriod}
                                        onEnableValue={onEnableValue}
                                        activeFundChildValue={activeFundChildValue}
                                        fundId={item.id}
                                        onClearValuesColumn={onClearValuesColumn}
                                        bgColor={
                                          selectedFund?.includes(item.id)
                                            ? item.backgroundColor
                                            : ''
                                        }
                                      />
                                      {rows.map((value, index) => (
                                        <RenderColumns
                                          table={table}
                                          rows={rows}
                                          isIndividualFund={isIndividualFund}
                                          key={index + value + table.id}
                                          index={index + 1}
                                          action={action}
                                          onDeleteSelectedCell={onDeleteSelectedCell}
                                          onFocusLocation={onFocusLocation}
                                          onDeleteRow={onDeleteRow}
                                        />
                                      ))}
                                    </>
                                  ) : (
                                    <List
                                      height={Math.min(
                                        funds.length === 1 ? 500 : 144,
                                        maxListHeight
                                      )}
                                      itemCount={rows.length + 1}
                                      itemSize={18}
                                      width="100%"
                                    >
                                      {({ index, style }) =>
                                        index === 0 ? (
                                          <RenderColumnLabels
                                            isStructured={isStructured}
                                            error={error}
                                            disabled={isLockedPeriod}
                                            table={table}
                                            onEnableValue={onEnableValue}
                                            activeFundChildValue={activeFundChildValue}
                                            fundId={item.id}
                                            onClearValuesColumn={onClearValuesColumn}
                                            bgColor={
                                              selectedFund?.includes(item.id)
                                                ? item.backgroundColor
                                                : ''
                                            }
                                            style={style}
                                          />
                                        ) : (
                                          <RenderColumns
                                            table={table}
                                            rows={rows}
                                            isIndividualFund={isIndividualFund}
                                            index={index}
                                            style={style}
                                            onDeleteSelectedCell={onDeleteSelectedCell}
                                            action={action}
                                            onFocusLocation={onFocusLocation}
                                            onDeleteRow={onDeleteRow}
                                          />
                                        )
                                      }
                                    </List>
                                  )}
                                </>
                              )}
                            </TableStyle>
                          );
                        })}
                      </FundChildrenContainer>
                    )}
                  </FundContainer>
                );
              })}
          </Body>
        )}
      </ScrollArea>

      <NewDeleteConfirmModal
        title="Delete Custom Field"
        description={`Are you sure you want to permanently delete this field?`}
        isOpen={!!selectedFieldId}
        onClose={() => setSelectedFieldId(null)}
        submitLabel="Yes, Delete"
        cancelLabel="Cancel"
        type="delete"
        onConfirm={callback => handleDeleteCustomField(null, callback)}
        isLoading={deleteCustomField.isLoading || deleteGlobalCustomField.isLoading}
      />
    </>
  );
};
